import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { translateSentence } from '../../../api/translationService';
import { useAuth } from '../../../context/AuthContext';

interface Props {
    /** The source text this icon translates — always required now, since the icon renders
     *  whenever there's something to translate, not only when an LLM-baked translation exists. */
    text: string;
    /** The LLM-generated native-language translation, when the admin "include translation"
     *  setting was on at generation time. Absent otherwise — the icon still renders and falls
     *  back to an on-demand Google Translate fetch of `text` instead. */
    native?: string;
    /** An already-fetched fallback translation, owned/persisted by the parent section (see
     *  useLessonPersistedState) so it survives navigating to another step and back — this
     *  component only holds the ephemeral loading/error state, not the fetched text itself. */
    fallback?: string;
    /** Called once a fallback fetch succeeds, so the parent can persist it into `fallback` on
     *  the next render. */
    onFallbackFetched?: (text: string) => void;
    /** Learning-language and native-language ISO codes, required only for the fallback fetch —
     *  if either is missing and `native`/`fallback` are also absent, the icon renders nothing. */
    sourceLanguage?: string;
    targetLanguage?: string;
    shown: boolean;
    onToggle: () => void;
}

// The small icon button + reveal block shared by every lesson section with translatable text.
// Two delivery modes, transparent to the caller: `native` (baked in at generation time) reveals
// instantly with no network call; missing `native` falls back to an on-demand client-side Google
// Translate fetch (see translationService.translateSentence). The fetch result itself is owned by
// the parent (`fallback`/`onFallbackFetched`) so it's cacheable across remounts; only the
// in-flight loading/error state lives here.
const TranslateToggle: React.FC<Props> = ({ text, native, fallback, onFallbackFetched, sourceLanguage, targetLanguage, shown, onToggle }) => {
    const { t } = useTranslation('lesson');
    const auth = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    // The fallback fetch always goes to Google Translate (see translateSentence) — it must
    // respect the same Preferences > Integrations > "Google Translate" opt-out every other
    // translation call site does, defaulting to enabled like those do.
    const googleTranslateEnabled = auth?.userSettings?.integration_settings?.google_translate ?? true;
    const canFallback = googleTranslateEnabled && !!text && !!sourceLanguage && !!targetLanguage;
    if (!native && !fallback && !canFallback) return null;

    // `||`, not `??`: a baked-in native translation can be an empty string (examples_native is a
    // parallel array whose individual entries may be ""), which must fall through to the fetched
    // fallback — the click handler below already treats "" as missing and fetches one.
    const display = native || fallback;

    const handleClick = () => {
        onToggle();
        if (native || fallback || loading || !canFallback) return;
        setLoading(true);
        setError(false);
        translateSentence(text, sourceLanguage as string, targetLanguage as string)
            .then((result) => onFallbackFetched?.(result))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    return (
        <>
            <button
                type="button"
                className="lesson-translate-btn"
                aria-label={t('sections.show_translation', 'Show translation')}
                onClick={handleClick}
            >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 5h8M8 3v2.5M6.5 5c0 4 2.5 7.5 6 9M9.5 8.5c1.5 2.5 4 4.5 7 5.5" />
                    <circle cx="17" cy="16" r="4" />
                    <path d="M13.5 14.5L16 20M20.5 14.5L18 20" />
                </svg>
            </button>
            {shown && loading && <div className="lesson-translation-native">{t('sections.translating', 'Translating…')}</div>}
            {shown && !loading && error && <div className="lesson-translation-native">{t('sections.translation_unavailable', 'Translation unavailable')}</div>}
            {shown && !loading && !error && display && <div className="lesson-translation-native">{display}</div>}
        </>
    );
};

export default TranslateToggle;
